import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../hooks/useIsMobile';
import { IconChevronD } from '../Icons';
import { landingMedia } from './media';

const FIRST_STEP_END = 0.55;
const MIN_FIRST_STEP_MS = 1800;
const REPLY_DELAY_MS = 1300;

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

const reveal = (visible: boolean): CSSProperties => ({
  overflow: 'hidden',
  maxHeight: visible ? 200 : 0,
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(12px)',
  transition: 'max-height .5s var(--ease), opacity .45s var(--ease), transform .45s var(--ease)',
});

export default function SayItSection() {
  const { t } = useTranslation('landing');
  const isNarrow = useIsMobile(720);
  const [step, setStep] = useState(0);
  const [replied, setReplied] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const fillRefs = [useRef<HTMLSpanElement>(null), useRef<HTMLSpanElement>(null)];
  const hintRef = useRef<HTMLDivElement>(null);
  const videoRefs = [useRef<HTMLVideoElement>(null), useRef<HTMLVideoElement>(null)];

  const exchanges = [
    { user: t('say.user1', 'Make a game of jetpack sheep flying through space.'), agent: t('say.agent1', 'On it. Building now.') },
    { user: t('say.user2', 'Add a new enemy to level 8.'), agent: t('say.agent2', 'Done. It’s in the game.') },
  ];

  useEffect(() => {
    let current = 0;
    let firstStepAt = 0;
    let holdTimer: number | undefined;
    let replyTimer: number | undefined;
    let raf = 0;

    const onScroll = () => {
      const el = sectionRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const progress = -r.top / Math.max(1, r.height - vh);

      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const [f1, f2] = fillRefs.map((f) => f.current);
        if (f1) f1.style.transform = `scaleX(${clamp01(progress / FIRST_STEP_END)})`;
        if (f2) f2.style.transform = `scaleX(${clamp01((progress - FIRST_STEP_END) / 0.4)})`;
        if (hintRef.current) hintRef.current.style.opacity = r.top < vh * 0.2 && progress < 0.97 ? '1' : '0';
      });

      let next = r.top > vh * 0.45 ? 0 : progress < FIRST_STEP_END ? 1 : 2;
      if (next === 2 && current < 2) {
        const held = current === 1 ? Date.now() - firstStepAt : 0;
        if (held < MIN_FIRST_STEP_MS) {
          window.clearTimeout(holdTimer);
          holdTimer = window.setTimeout(onScroll, MIN_FIRST_STEP_MS + 50 - held);
          if (current === 1) return;
          next = 1;
        }
      }
      if (next === 1 && current !== 1) firstStepAt = Date.now();
      if (next === current) return;

      current = next;
      window.clearTimeout(replyTimer);
      setStep(next);
      setReplied((r0) => Math.min(r0, Math.max(0, next - 1)));
      if (next > 0) {
        replyTimer = window.setTimeout(() => setReplied(next), REPLY_DELAY_MS);
        const video = videoRefs[next - 1].current;
        if (video) {
          video.currentTime = 0;
          video.play().catch(() => {});
        }
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.clearTimeout(holdTimer);
      window.clearTimeout(replyTimer);
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const panelMinHeight = isNarrow ? 'clamp(150px, 24svh, 260px)' : 'clamp(260px, 40vh, 420px)';
  const activeVideo = step || 1;

  return (
    <section id="demo" ref={sectionRef} style={{ position: 'relative', height: '260vh', marginTop: 40 }}>
      <div
        style={{
          position: 'sticky',
          top: 'var(--header-h)',
          height: 'calc(100svh - var(--header-h))',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: 'clamp(12px, 4svh, 32px) var(--gutter)',
        }}
      >
        <h2
          className="t-h2"
          style={{ fontSize: 'min(clamp(30px, 4.2vw, 56px), 8svh)', margin: '0 0 clamp(12px, 4svh, 48px)', textAlign: 'center' }}
        >
          {t('say.titleA', 'Say it.')}
          <br />
          <span className="t-accent">{t('say.titleB', 'See it in the game.')}</span>
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isNarrow ? 'minmax(0, 1fr)' : 'minmax(0, 0.85fr) minmax(0, 1.4fr)',
            gap: 'clamp(20px, 3vw, 44px)',
            alignItems: 'stretch',
            minHeight: 0,
          }}
        >
          <div className="panel" style={{ display: 'flex', flexDirection: 'column', minWidth: 0, borderRadius: 'var(--r-xl)' }}>
            <div className="panel-head">
              <img src={landingMedia.logo} alt="" style={{ width: 18, height: 18, objectFit: 'contain' }} />
              {t('common:app.name', 'Entire')}
              <span className="label" style={{ marginLeft: 'auto', letterSpacing: '0.16em' }}>
                <span className="dot ok" />
                {t('say.online', 'Online')}
              </span>
            </div>
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                gap: 12,
                padding: 'clamp(12px, 3svh, 22px) 18px',
                minHeight: panelMinHeight,
              }}
            >
              {exchanges.map((x, i) => {
                const n = i + 1;
                const visible = step === n;
                const done = replied >= n;
                return (
                  <div key={n} style={{ display: 'contents' }}>
                    <div className="bubble-row user" style={reveal(visible)}>
                      <div className="bubble user">{x.user}</div>
                    </div>
                    <div className="bubble-row agent" style={reveal(visible)}>
                      <span className="avatar">
                        <img src={landingMedia.logo} alt="" />
                      </span>
                      <div className="bubble agent" style={{ minHeight: 22, display: 'flex', alignItems: 'center' }}>
                        {done ? (
                          <span>{x.agent}</span>
                        ) : (
                          <span className="typing" aria-hidden>
                            <span />
                            <span />
                            <span />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div
            style={{
              position: 'relative',
              minWidth: 0,
              minHeight: panelMinHeight,
              borderRadius: 'var(--r-xl)',
              overflow: 'hidden',
              background: 'var(--surface)',
              border: '1px solid var(--line-2)',
            }}
          >
            {landingMedia.say.map((m, i) => {
              const on = activeVideo === i + 1;
              return (
                <div
                  key={m.video}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    opacity: on ? 1 : 0,
                    transform: on ? 'scale(1)' : 'scale(1.08)',
                    filter: on ? 'blur(0px)' : 'blur(10px)',
                    transition: 'opacity var(--dur-cross) var(--ease), transform .9s var(--ease), filter var(--dur-cross) var(--ease)',
                    backgroundImage: `url('${m.poster}')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <video ref={videoRefs[i]} src={m.video} className="media-fill" preload="metadata" autoPlay muted loop playsInline />
                </div>
              );
            })}
          </div>
        </div>
        <div
          ref={hintRef}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 14,
            marginTop: 'clamp(10px, 2.5svh, 24px)',
            opacity: 0,
            transition: 'opacity var(--dur) var(--ease)',
          }}
        >
          <div style={{ display: 'flex', gap: 6 }}>
            {fillRefs.map((ref, i) => (
              <span key={i} className="track" style={{ width: 48 }}>
                <span ref={ref} style={{ transform: 'scaleX(0)' }} />
              </span>
            ))}
          </div>
          <span className="label" style={{ gap: 6, letterSpacing: '0.16em' }}>
            {t('say.keepScrolling', 'Keep scrolling')}
            <span style={{ display: 'inline-flex', animation: 'nudge 1.6s ease-in-out infinite' }}>
              <IconChevronD size={14} />
            </span>
          </span>
        </div>
      </div>
    </section>
  );
}
